import { useEffect, useState } from 'react'

import { Allotment } from 'allotment'
import { clsx } from 'clsx'
import { Outlet, useOutletContext } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'

import { ConsumerSideNavigation } from '~/portal/components/ConsumerSideNavigation'
import { useWindowDimensions } from '~/portal/hooks/useWindowDimensions'

const SIDE_NAV_WIDTH = 250
const ALLOTMENT_WIDTH_KEY = 'docs.allotment.width'

export const DesktopView = ({ tableOfContents, branding }) => {
  const [savedWidth, setSavedWidth] = useState<number | null>(null)

  /*
    Because <Allotment /> isn't rendered server side, we have to render it only after page is hydrated,
    otherwise styles won't be loaded and we'll see a few seconds of sidenav with page/api content being stacked together.
  */

  const isHydrated = useHydrated()

  useEffect(() => {
    if (isHydrated && globalThis.window !== undefined) {
      const stored = globalThis.window.localStorage.getItem(ALLOTMENT_WIDTH_KEY)
      if (stored && !Number.isNaN(Number(stored))) {
        setSavedWidth(Number(stored))
      } else {
        setSavedWidth(null)
      }
    }
  }, [isHydrated])

  const { width } = useWindowDimensions()
  const maxWidth = width ? width / 3 : 375

  const { isPreview } = useOutletContext<any>()

  const { accentColor, fontName } = branding

  if (!isHydrated) {
    return <div className="sp-content h-full" style={{ fontFamily: fontName }} />
  }

  const allotmentKey = savedWidth !== null ? `allotment-${savedWidth}` : 'allotment-default'
  const defaultSizes = savedWidth !== null ? [savedWidth, width - savedWidth] : [SIDE_NAV_WIDTH, width - SIDE_NAV_WIDTH]

  const handleAllotmentChange = (sizes: number[]) => {
    if (globalThis.window !== undefined && sizes.length > 0) {
      globalThis.window.localStorage.setItem(ALLOTMENT_WIDTH_KEY, String(sizes[0]))
    }
  }

  return (
    <div
      data-testid="docs-container"
      className={clsx(
        isPreview ? 'h-[calc(100%_-_120px)]' : 'h-[calc(100%_-_60px)]',
        !isHydrated && 'grid grid-cols-[15.625rem_minmax(0,_1fr)] w-full',
      )}
      style={{ fontFamily: fontName }}
    >
      <Allotment
        key={allotmentKey}
        className="w-full h-full allotment-overflow-visible"
        defaultSizes={defaultSizes}
        onChange={handleAllotmentChange}
      >
        <Allotment.Pane
          minSize={SIDE_NAV_WIDTH}
          preferredSize={SIDE_NAV_WIDTH}
          maxSize={maxWidth}
          visible={tableOfContents.length > 0}
        >
          <ConsumerSideNavigation
            tableOfContents={tableOfContents}
            accentColor={accentColor}
          />
        </Allotment.Pane>
        <Allotment.Pane>
          <section className="sp-content h-full overflow-hidden"><Outlet /></section>
        </Allotment.Pane>
      </Allotment>
    </div>
  )
}